import CustomButton from 'components/Buttons/CustomButton'
import Skeleton from 'components/Skeleton'
import { useEffect, useState } from 'react'
import { Check, ChevronDown, ChevronUp, Package, Search } from 'react-feather'
import { useIntl } from 'react-intl'
import {
  Badge,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from 'reactstrap'

/**
 * ModalGroupItemPicker - Component untuk memilih item individual dari grup inventori
 *
 * @param {boolean} isOpen - Status modal terbuka atau tidak
 * @param {function} toggle - Function untuk toggle modal
 * @param {object} selectedGroup - Grup inventori yang dipilih
 * @param {array} groupItems - List item dalam grup
 * @param {boolean} isLoading - Status loading saat fetch items
 * @param {function} onConfirm - Callback ketika user konfirmasi pilihan
 * @param {function} onSelectAll - Callback untuk pilih semua (behavior lama)
 * @param {object} intl - Intl object untuk formatting
 */
const ModalGroupItemPicker = ({
  isOpen,
  toggle,
  selectedGroup,
  groupItems = [],
  isLoading = false,
  onConfirm,
  onSelectAll,
  initialSelectedItemIds = [],
}) => {
  const intl = useIntl()
  const [selectedItems, setSelectedItems] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [sortOrder, setSortOrder] = useState('asc') // 'asc' or 'desc'
  const [sortBy, setSortBy] = useState('name') // 'name', 'amount', 'warehouse'

  // Helper to safely extract string value from potentially object fields
  const safeString = (value, fallback = '-') => {
    if (!value) return fallback
    if (typeof value === 'string') return value
    if (typeof value === 'number') return String(value)
    if (typeof value === 'object') {
      return (
        value?.name || value?.value || value?.label || value?.title || fallback
      )
    }
    return fallback
  }

  // Helper to extract unit string from unit object or string
  const getUnitString = (item) => {
    const unit = item?.unit || item?.item?.unit
    return safeString(unit, 'Kg')
  }

  // Helper to extract warehouse string
  const getWarehouseString = (item) => {
    const warehouse =
      item?.warehouse || item?.item?.warehouse || item?.warehouseName
    return safeString(warehouse, '-')
  }

  // Helper to extract item name
  const getItemName = (item) => {
    const name = item?.nameItem || item?.item?.nameItem || item?.name
    return safeString(name, '-')
  }

  // Helper to extract owner code
  const getOwnerCode = (item) => {
    const code = item?.ownerCode || item?.item?.ownerCode
    return safeString(code, '-')
  }

  // Helper to extract lot code
  const getLotCode = (item) => {
    const code = item?.lotCode || item?.item?.lotCode
    return safeString(code, '-')
  }

  useEffect(() => {
    if (!isOpen) return

    const preselectedIds = (
      Array.isArray(initialSelectedItemIds) ? initialSelectedItemIds : []
    )
      .filter((id) => id !== null && id !== undefined)
      .map((id) => String(id))

    if (preselectedIds.length > 0 && Array.isArray(groupItems)) {
      const preselectedItems = groupItems.filter((item) =>
        preselectedIds.includes(String(item?.id)),
      )
      setSelectedItems(preselectedItems)
    } else {
      setSelectedItems([])
    }

    setSearchQuery('')
  }, [isOpen, groupItems, initialSelectedItemIds])

  const handleToggleItem = (item) => {
    const itemId = item.id
    const isSelected = selectedItems.some((i) => i.id === itemId)

    if (isSelected) {
      setSelectedItems(selectedItems.filter((i) => i.id !== itemId))
    } else {
      setSelectedItems([...selectedItems, item])
    }
  }

  const handleSelectAll = () => {
    if (selectedItems.length === filteredItems.length) {
      setSelectedItems([])
    } else {
      setSelectedItems([...filteredItems])
    }
  }

  const handleConfirm = () => {
    if (selectedItems.length > 0) {
      onConfirm(selectedItems)
    }
  }

  const handleSelectAllAndClose = () => {
    onSelectAll()
  }

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
  }

  const filteredItems = groupItems
    .filter((item) => {
      if (!searchQuery) return true
      const query = searchQuery.toLowerCase()
      const nameMatch = getItemName(item).toLowerCase().includes(query)
      const warehouseMatch = getWarehouseString(item)
        .toLowerCase()
        .includes(query)
      const ownerCodeMatch = getOwnerCode(item).toLowerCase().includes(query)
      const lotCodeMatch = getLotCode(item).toLowerCase().includes(query)
      return nameMatch || warehouseMatch || ownerCodeMatch || lotCodeMatch
    })
    .sort((a, b) => {
      let aVal, bVal
      switch (sortBy) {
        case 'name':
          aVal = getItemName(a).toLowerCase()
          bVal = getItemName(b).toLowerCase()
          break
        case 'amount':
          aVal = a.amount || 0
          bVal = b.amount || 0
          break
        case 'warehouse':
          aVal = getWarehouseString(a).toLowerCase()
          bVal = getWarehouseString(b).toLowerCase()
          break
        default:
          aVal = ''
          bVal = ''
      }
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
      }
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
    })

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null
    return sortOrder === 'asc' ? (
      <ChevronUp size={14} />
    ) : (
      <ChevronDown size={14} />
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      style={{ maxWidth: '700px' }}
      centered
      data-testid="sell-form-modal-group-picker"
    >
      <ModalHeader toggle={toggle}>
        <div className="d-flex align-items-center gap-2">
          <Package size={20} className="text-primary" />
          <span>Pilih Item dari Grup</span>
        </div>
      </ModalHeader>
      <ModalBody
        style={{
          overflowY: 'auto',
          minHeight: '300px',
          maxHeight: '60vh',
          padding: 16,
        }}
      >
        {/* Group Info Header */}
        <div className="mb-3 p-3 bg-primary-50 border border-primary rounded">
          <Row>
            <Col md={6}>
              <div className="mb-2">
                <small className="text-muted">Nama Grup</small>
                <div className="fw-bold">
                  {selectedGroup?.nameItem || selectedGroup?.name || '-'}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-2">
                <small className="text-muted">Total Item dalam Grup</small>
                <div className="fw-bold">
                  {groupItems?.length} item
                  {/* {intl.formatNumber(totalGroupAmount, {
                    useGrouping: 'always',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })}{' '}
                  {getUnitString(groupItems[0])} */}
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Search & Filter */}
        <div className="mb-3">
          <InputGroup>
            <InputGroupText className="bg-transparent">
              <Search size={16} />
            </InputGroupText>
            <Input
              type="text"
              placeholder="Cari item berdasarkan nama, gudang, kode, atau lot..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>
        </div>

        {/* Selection Summary */}
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <div>
            <Badge color={selectedItems.length > 0 ? 'primary' : 'secondary'}>
              {selectedItems.length} item dipilih
            </Badge>
            {/* {selectedItems.length > 0 && (
              <span className="ms-2 text-muted small">
                Total:{' '}
                {intl.formatNumber(totalSelectedAmount, {
                  useGrouping: 'always',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })}{' '}
                {getUnitString(groupItems[0])}
              </span>
            )} */}
          </div>
          <CustomButton
            color="link"
            size="sm"
            onClick={handleSelectAll}
            className="p-0"
          >
            {selectedItems.length === filteredItems.length
              ? 'Batal Pilih Semua'
              : 'Pilih Semua'}
          </CustomButton>
        </div>

        {isLoading ? (
          <div className="d-flex flex-column justify-content-center align-items-center gap-3 py-5 my-4">
            <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
            <h5 className="text-muted mt-2">Memuat item dalam grup...</h5>
          </div>
        ) : groupItems.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <Package size={48} className="mb-3 opacity-50" />
            <p>Tidak ada item dalam grup ini</p>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div
              className="d-flex align-items-center p-2 bg-light rounded mb-2"
              style={{ fontSize: '0.85em' }}
            >
              <div style={{ width: '40px' }}></div>
              <div
                className="flex-grow-1 pointer d-flex align-items-center gap-1"
                onClick={() => toggleSort('name')}
                style={{ cursor: 'pointer' }}
              >
                <strong>Nama Item</strong>
                <SortIcon field="name" />
              </div>
              <div
                className="text-end pointer d-flex align-items-center justify-content-end gap-1"
                style={{ width: '120px', cursor: 'pointer' }}
                onClick={() => toggleSort('amount')}
              >
                <strong>Jumlah</strong>
                <SortIcon field="amount" />
              </div>
              <div
                className="text-end pointer d-flex align-items-center justify-content-end gap-1"
                style={{ width: '150px', cursor: 'pointer' }}
                onClick={() => toggleSort('warehouse')}
              >
                <strong>Gudang</strong>
                <SortIcon field="warehouse" />
              </div>
            </div>

            {/* Item List */}
            <div className="d-flex flex-column gap-2">
              {filteredItems.map((item) => {
                const isSelected = selectedItems.some((i) => i.id === item.id)
                return (
                  <div
                    key={item.id}
                    className={`d-flex align-items-center p-3 rounded border pointer transition-all ${
                      isSelected
                        ? 'bg-primary-50 border-primary'
                        : 'bg-white border-light hover-bg-light'
                    }`}
                    onClick={() => handleToggleItem(item)}
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {/* Checkbox */}
                    <div
                      style={{ width: '40px' }}
                      className="d-flex justify-content-center"
                    >
                      <div
                        className={`d-flex align-items-center justify-content-center rounded ${
                          isSelected
                            ? 'bg-primary text-white'
                            : 'border border-secondary'
                        }`}
                        style={{
                          width: '18px',
                          height: '18px',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {isSelected && <Check size={14} />}
                      </div>
                    </div>

                    {/* Item Info */}
                    <div className="flex-grow-1">
                      <div className="fw-semibold">{getItemName(item)}</div>
                      <div
                        className="text-muted small"
                        style={{ fontSize: '0.8em' }}
                      >
                        <span>Kode Inventori: {getOwnerCode(item)}</span>
                        <span className="mx-2">•</span>
                        <span>Lot ID: {getLotCode(item)}</span>
                      </div>
                      {(item?.projectLabel || item?.project?.label) && (
                        <div style={{ fontSize: '0.8em', marginTop: '2px' }}>
                          <span
                            className="badge"
                            style={{
                              backgroundColor: '#E8F5E9',
                              color: '#2E7D32',
                              fontWeight: 500,
                              fontSize: '0.85em',
                              padding: '2px 8px',
                              borderRadius: '4px',
                            }}
                          >
                            Project: {item?.projectLabel || item?.project?.label}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Amount */}
                    <div
                      className="text-end fw-semibold"
                      style={{ width: '120px' }}
                    >
                      {intl.formatNumber(item.amount || 0, {
                        useGrouping: 'always',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      })}{' '}
                      <span className="text-muted small">
                        {getUnitString(item)}
                      </span>
                    </div>

                    {/* Warehouse */}
                    <div
                      className="text-end text-muted small"
                      style={{ width: '150px' }}
                    >
                      {getWarehouseString(item)}
                    </div>
                  </div>
                )
              })}
            </div>

            {filteredItems.length === 0 && searchQuery && (
              <div className="text-center py-4 text-muted">
                <p>
                  Tidak ada item yang cocok dengan pencarian "{searchQuery}"
                </p>
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter className="d-flex justify-content-end">
        <div className="d-flex gap-2">
          <CustomButton
            className="btn btn-cancel"
            onClick={toggle}
            testId="sell-form-modal-group-picker-close"
          >
            Batal
          </CustomButton>
          <CustomButton
            color="primary"
            onClick={handleConfirm}
            disabled={selectedItems.length === 0}
            testId="sell-form-modal-group-picker-submit"
          >
            Konfirmasi ({selectedItems.length} item)
          </CustomButton>
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default ModalGroupItemPicker
